import { TextField, Typography, useTheme } from "@mui/material";
import React, { PropsWithChildren, type JSX } from 'react';
import { Controller, useFormContext } from "react-hook-form";
import { FormFieldPropsBase } from "../form/Form";
import NumberFormatCustom from "../../numeric-format-custom/NumericFormatCustom";


export type FormInputProps = {
    rules?: any;
    multiline?: boolean;
    rows?: number;
    numeric?: boolean;
    decimals?: number;
    format?: string;
    fileUpload?: boolean;
    fileAcceptTypes?: string;
    startAdornment?: JSX.Element;
    endAdornment?: JSX.Element;
    units?: string;
    color?: string;
    size?: "small" | "medium" | undefined;
    type?: React.InputHTMLAttributes<unknown>['type'];
    boldOnDirty?: boolean;
    minLength?: number;
    maxLength?: number;
    onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>,
    onBlur?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>,
    placeHolder?: string;
    hasError?: boolean;
    backgroundColor?: string;
} & FormFieldPropsBase;

export default function FormInput({
    name,
    id,
    testId,
    label,
    ariaLabel,
    readOnly,
    helperText,
    rules,
    multiline,
    rows,
    sx,
    numeric,
    decimals,
    format,
    fileUpload,
    fileAcceptTypes,
    startAdornment,
    endAdornment,
    disabled,
    units,
    color,
    size,
    type,
    boldOnDirty,
    minLength,
    maxLength,
    onChange,
    onBlur,
    placeHolder,
    hasError,
    backgroundColor,
    shouldUnregister,
    showID

}: PropsWithChildren<FormInputProps>): JSX.Element {
    const { control } = useFormContext();
    const theme = useTheme();

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={
                ({
                    field: { ref, name, value, onChange: onFieldChange, onBlur: onFieldBlur },
                    fieldState: { invalid, isTouched, isDirty, error },
                    formState
                }: {
                    field: any;
                    fieldState: any;
                    formState: any;
                }) => (
                    <TextField
                        variant="filled"
                        id={id}
                        name={name}
                        data-testid={testId}
                        aria-label={ariaLabel ?? undefined}
                        multiline={multiline}
                        rows={rows}
                        label={label ?? undefined}
                        error={invalid || hasError}
                        helperText={invalid ? error.message : helperText ?? undefined}
                        onBlur={(e) => {
                            onFieldBlur(e);
                            onBlur?.(e);
                        }}
                        onChange={
                            (e) => {
                                if(fileUpload) {
                                    const fileName = (e.target as HTMLInputElement).files?.[0]?.name;
                                    onFieldChange(fileName);
                                    onChange?.(e);
                                } else {
                                    onFieldChange(e);
                                    onChange?.(e);
                                }
                            }
                        }
                        inputRef={ref}
                        value={fileUpload
                            ? undefined
                            : value ?? ""
                        }
                        sx={{
                            marginTop: 0,
                            ...sx
                        }}
                        disabled={disabled}
                        size={size}
                        slotProps={{
                            input: {
                                readOnly: readOnly,
                                sx: {
                                    color: color,
                                    fontWeight: boldOnDirty && isDirty ? 700 : 400,
                                    backgroundColor: backgroundColor,
                                },
                                inputComponent: numeric ? NumberFormatCustom as any : undefined,
                                inputProps: {
                                    accept: fileUpload && fileAcceptTypes ? fileAcceptTypes : undefined,
                                    decimals: decimals,
                                    //data-pattern: `^[+-]?\d*\.?\d{0,${decimals ?? 2}}$`
                                    pattern: numeric ? encodeURIComponent(`^[-+]?\\d*\\.?\\d{0,${decimals ?? 2}}$`) : undefined,
                                    // User Story 51313: Research - Ongoing Virtual Keyboard Issues
                                    // inputMode: numeric ? decimals ? decimals === 0 ? 'numeric' : 'decimal' : 'decimal' : undefined,
                                    inputMode: "text",
                                    format: format,
                                    type: fileUpload
                                        ? 'file'
                                        : type
                                            ? type
                                            // : numeric
                                            //     ? 'number'
                                                : undefined,
                                    minLength: minLength,
                                    maxLength: maxLength,
                                    placeholder: placeHolder
                                    
                                },
                                startAdornment: startAdornment,
                                endAdornment: showID ? <Typography sx={{
                                                color: theme.palette.primary.light,
                                                position: 'absolute', 
                                                fontSize: 'small',
                                                top: '6px', 
                                                right: '12px'
                                            }}>{`[${id}]`}
                                            </Typography> : 
                                
                                endAdornment ?? units
                            },

                            inputLabel: {
                                shrink: true,
                                sx: {
                                    fontWeight: boldOnDirty && isDirty ? 700 : 400
                                }
                            }
                        }} />
                )
            }
            shouldUnregister={shouldUnregister}
        />
    );
}